import {
  type CanvasElementFrom,
  type FilterBuilderProps,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type SearchAndFiltersProps,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithColumns,
  type WithElementSearchAndFilters,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type CanvasElementWithFeaturesConfigProps,
  type ElementLabelProps,
  type ElementTitleStyleProps,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
  SearchAndFiltersMode,
} from '@blaze-shared/elements-domain';
import { DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { DataFilterDefinitionMapper } from './ai/data-filter.definition-mapper';
import { DataFilterElementHandler } from './ai/data-filter.element-handler';
import { DataFilterChildrenHandlerService } from './data-filter-children-handler.service';
import { DataFilterComponent } from './data-filter.component';
import { dataFilterV2Migration } from './migrations/002-data-filter.migration';

export type DataFilterElement = CanvasElementFrom<'dataFilter'>;
export type DataFilterDef = WithProps<DataFilterPropsModel> &
  WithColumns &
  WithElementSearchAndFilters &
  WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    dataFilter: DataFilterDef;
  }
}

const CONTAINER_HEIGHT = 60;

export function dataFilterDefinition(): CanvasElementDefinition<'dataFilter'> {
  return {
    type: 'dataFilter',
    componentType: DataFilterComponent,
    childrenHandler: DataFilterChildrenHandlerService,
    isListed: true,
    name: 'Data Filter',
    width: { mode: 'full' },
    // By default the element should have a min-height and grow automatically if needed
    height: { mode: 'value', value: CONTAINER_HEIGHT, fixed: false },
    ioConfig: getIoConfig(),
    propsConfig: getPropsConfig(),
    defaults: getDefaults(),
    initial: getInitial(),
    styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
    metaUiFeatures: { type: 'container' },
    ai: {
      name: 'dataFilter',
      handler: () => DataFilterElementHandler,
      definitionMapper: () => DataFilterDefinitionMapper,
    },
    version: 2,
    migrations: {
      2: dataFilterV2Migration,
    },
  };
}

registerElementDefinition(dataFilterDefinition());

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'query', schema: DEFAULT_SCHEMA },
      { eventType: 'change', schema: DEFAULT_SCHEMA },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  CanvasElementWithFeaturesConfigProps &
  SearchAndFiltersProps &
  FilterBuilderProps &
  StyleConfigProps &
  VisibilityElementProps;

export type DataFilterPropsModel = PropsModel & {
  content: {
    title: ElementLabelProps;
  };
  style: {
    container: {
      title: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      fill: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
      visualEffect: PropertiesConfigInputVisualEffectModel;
    };
    titleBlock: ElementTitleStyleProps;
  };
};

function getDefaults(): PropsOf<'dataFilter'> {
  return {
    content: {
      title: {
        enabled: false,
        align: 'left',
        label: 'Filter',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      searchAndFilters: {
        injectToElement: true,
        searchEnabled: false,
        mode: SearchAndFiltersMode.PREDEFINED,
        filters: [],
      },
      filterBuilderConfig: {},
    },
    style: {
      container: {
        title: { color1: '$t.title', selectorType: 'solid' },
        // Doubled spacingExternal reference is intentional here
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingExternal' },
        outlineWidth: '$t.containerOutlineWidth',
        fill: { color1: '$t.fillExternal', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'dataFilter'> {
  return {
    content: {
      searchAndFilters: {
        injectToElement: true,
        searchEnabled: true,
        mode: SearchAndFiltersMode.PREDEFINED,
        filters: [],
      },
    },
    featuresConfig: {
      fieldsLabelGen: 2,
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('dataFilter', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Data',
        [],
        [
          epc.fields.selectDataSourceField(
            {
              key: 'value',
              label: 'Data source',
              sources: 'list',
              bindings: [],
            },
            'data-filter',
          ),
        ],
        { enabledByDefault: true },
      ),
      epc.subsections.complexSearchAndFiltersSubsection({ label: 'Search' }),

      epc.subsections.visibilitySubsection(),

      epc.subsections.titleContentSubsection('content'),
    ]),

    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.container', 'Padding & Margin', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.container', 'Appearance', [
          epc.fields.colorField('style.container.fill', 'Fill', {}),
          epc.fields.colorField('style.container.outline', 'Outline', { allowGradient: false }),
          epc.fields.sliderField('style.container.outlineWidth', 'Width', [
            { min: 0, max: 10, unit: 'px' },
          ]),
          epc.fields.radiusField('style.container.radius'),
          ...epc.fields.visualEffectField('style.container.visualEffect'),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.container.title',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
